// AGC: Game Guide 2 - Ultra-condensed single-page version
// AGC: Compact design fitting on one screen without scrolling
import React, { useState, useEffect } from 'react'
import { ChevronDown, ChevronUp, BookOpen } from 'lucide-react'
import { playSoundSafe } from '../utils/audio'

interface GameGuide2Props {
  onFullVersion?: () => void
}

// AGC: Compact accordion item
interface AccordionItemProps {
  id: string
  title: string
  icon?: string
  children: React.ReactNode
  isOpen: boolean
  onToggle: () => void
  onCollapse?: () => void
}

const AccordionItem: React.FC<AccordionItemProps> = ({ id, title, icon, children, isOpen, onToggle, onCollapse }) => {
  const toggle = () => {
    playSoundSafe('/sounds/click.mp3', 0.2)
    onToggle()
  }

  const handleCollapse = () => {
    playSoundSafe('/sounds/click.mp3', 0.2)
    onToggle()
    if (onCollapse) {
      setTimeout(() => onCollapse(), 100)
    }
  }

  return (
    <div className="border-b border-slate-700/50" id={id}>
      <button
        onClick={toggle}
        className="w-full flex items-center justify-between p-2 text-left hover:bg-slate-800/50 transition-colors"
      >
        <div className="flex items-center gap-2">
          {icon && <span className="text-lg">{icon}</span>}
          <h3 className="text-sm font-semibold text-white">{title}</h3>
        </div>
        {isOpen ? (
          <ChevronUp className="w-4 h-4 text-slate-400 flex-shrink-0" />
        ) : (
          <ChevronDown className="w-4 h-4 text-slate-400 flex-shrink-0" />
        )}
      </button>
      {isOpen && (
        <div className="px-3 pb-2 text-xs text-slate-300 space-y-2">
          {children}
          <div className="pt-1 border-t border-slate-700/50 mt-2">
            <button
              onClick={handleCollapse}
              className="w-full flex items-center justify-center gap-1 py-1 text-xs text-slate-400 hover:text-slate-300 transition-colors"
            >
              <ChevronUp className="w-3 h-3" />
              <span>Collapse</span>
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

// AGC: Compact table of contents
const TableOfContents: React.FC<{ sections: Array<{ id: string; title: string; icon?: string }>; onNavigate: (id: string) => void; onFullVersion?: () => void }> = ({ sections, onNavigate, onFullVersion }) => {
  const [isExpanded, setIsExpanded] = useState(false)

  return (
    <div className="bg-slate-800/50 rounded-lg p-2 mb-2 border border-slate-700">
      <button
        onClick={() => {
          playSoundSafe('/sounds/click.mp3', 0.2)
          setIsExpanded(!isExpanded)
        }}
        className="w-full flex items-center justify-between"
      >
        <div className="flex items-center gap-2">
          <BookOpen className="w-4 h-4 text-blue-400" />
          <h2 className="text-sm font-semibold text-white">Table of Contents</h2>
        </div>
        {isExpanded ? (
          <ChevronUp className="w-4 h-4 text-slate-400" />
        ) : (
          <ChevronDown className="w-4 h-4 text-slate-400" />
        )}
      </button>
      {isExpanded && (
        <>
          <div className="mt-2 grid grid-cols-2 gap-1">
            {sections.map((section) => (
              <button
                key={section.id}
                onClick={() => {
                  playSoundSafe('/sounds/click.mp3', 0.2)
                  onNavigate(section.id)
                  setIsExpanded(false)
                }}
                className="text-left px-2 py-1 rounded hover:bg-slate-700/50 transition-colors text-xs text-slate-300 flex items-center gap-1"
              >
                {section.icon && <span className="text-xs">{section.icon}</span>}
                <span className="truncate">{section.title}</span>
              </button>
            ))}
          </div>
          {onFullVersion && (
            <div className="mt-2 pt-2 border-t border-slate-700/50">
              <button
                onClick={() => {
                  playSoundSafe('/sounds/click.mp3', 0.3)
                  onFullVersion()
                }}
                className="w-full px-3 py-1.5 rounded-lg bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 transition-all transform hover:scale-105 text-xs font-semibold text-white"
              >
                📖 View Full Version
              </button>
            </div>
          )}
        </>
      )}
    </div>
  )
}

/**
 * AGC: Ultra-condensed game guide - fits on one screen
 */
export const GameGuide2: React.FC<GameGuide2Props> = ({ onFullVersion }) => {
  const [openSections, setOpenSections] = useState<Set<string>>(new Set())

  useEffect(() => {
    // AGC: Scroll to table of contents on page load - ensure it's visible
    const scrollToTOC = () => {
      window.scrollTo({ top: 0, behavior: 'instant' })
      setTimeout(() => {
        const tocElement = document.getElementById('table-of-contents')
        if (tocElement) {
          tocElement.scrollIntoView({ behavior: 'instant', block: 'start' })
          window.scrollBy(0, -20)
        }
      }, 50)
    }
    scrollToTOC()
    // AGC: Also try after a longer delay to ensure page is fully rendered
    setTimeout(scrollToTOC, 300)
  }, [])

  const navigateToSection = (id: string) => {
    setOpenSections(new Set([id])) // AGC: Single-open accordion
    setTimeout(() => {
      const element = document.getElementById(id)
      if (element) element.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, 100)
  }

  const toggleSection = (id: string) => {
    setOpenSections((prev) => {
      const next = new Set<string>()
      // AGC: If section is already open, close it. Otherwise, open only this section (single-open accordion)
      if (!prev.has(id)) {
        next.add(id)
      }
      return next
    })
  }

  const isSectionOpen = (id: string) => openSections.has(id)

  const scrollToTableOfContents = () => {
    const tocElement = document.getElementById('table-of-contents')
    if (tocElement) {
      tocElement.scrollIntoView({ behavior: 'smooth', block: 'start' })
      setTimeout(() => window.scrollBy(0, -20), 300)
    }
  }

  const sections = [
    { id: 'rock-paper-scissors', title: 'Hero: Rock-Paper-Scissors', icon: '✂️' },
    { id: 'elements', title: 'Elements', icon: '🌊' },
    { id: 'buffs', title: 'Buff NFT', icon: '🛡️' },
    { id: 'potions', title: 'Potion NFT', icon: '🧪' },
    { id: 'rarity', title: 'Rarity', icon: '💎' },
    { id: 'leveling', title: 'Leveling', icon: '📈' },
    { id: 'damage', title: 'Damage', icon: '⚔️' },
    { id: 'economics', title: 'Economics', icon: '💰' },
    { id: 'tournaments', title: 'Tournaments', icon: '🏆' },
    { id: 'tips', title: 'Tips', icon: '🎯' },
  ]

  return (
    <main className="pt-[72px] pb-[88px] px-2 min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950">
      <div className="max-w-4xl mx-auto">
        <div id="table-of-contents">
          <TableOfContents sections={sections} onNavigate={navigateToSection} onFullVersion={onFullVersion} />
        </div>

        <div className="bg-slate-800/50 rounded-lg border border-slate-700 overflow-hidden">
          <AccordionItem
            id="rock-paper-scissors"
            title="Hero: Rock-Paper-Scissors"
            icon="✂️"
            isOpen={isSectionOpen('rock-paper-scissors')}
            onToggle={() => toggleSection('rock-paper-scissors')}
            onCollapse={scrollToTableOfContents}
          >
            <div className="space-y-1.5">
              <div className="flex items-center justify-center gap-2 text-xs">
                <span className="text-gray-300">🗿 ROCK</span>
                <span className="text-gray-500">→</span>
                <span className="text-blue-400">📄 PAPER</span>
                <span className="text-gray-500">→</span>
                <span className="text-red-400">✂️ SCISSORS</span>
                <span className="text-gray-500">→</span>
                <span className="text-gray-300">🗿</span>
              </div>
              <div className="grid grid-cols-3 gap-1.5 text-xs">
                <div className="bg-green-900/30 border border-green-700 rounded p-1.5">
                  <p className="font-semibold text-green-400 text-xs">WIN 30dmg</p>
                </div>
                <div className="bg-amber-900/30 border border-amber-700 rounded p-1.5">
                  <p className="font-semibold text-amber-400 text-xs">DRAW 15dmg</p>
                </div>
                <div className="bg-red-900/30 border border-red-700 rounded p-1.5">
                  <p className="font-semibold text-red-400 text-xs">LOSE 10dmg</p>
                </div>
              </div>
            </div>
          </AccordionItem>

          <AccordionItem
            id="elements"
            title="Elements"
            icon="🌊"
            isOpen={isSectionOpen('elements')}
            onToggle={() => toggleSection('elements')}
            onCollapse={scrollToTableOfContents}
          >
            <div className="space-y-1.5">
              <div className="flex items-center justify-center gap-1.5 text-xs">
                <span className="text-blue-400">💧 WATER</span>
                <span className="text-gray-500">→</span>
                <span className="text-red-400">🔥 FIRE</span>
                <span className="text-gray-500">→</span>
                <span className="text-cyan-300">❄️ ICE</span>
                <span className="text-gray-500">→</span>
                <span className="text-green-400">💨 WIND</span>
                <span className="text-gray-500">→</span>
                <span className="text-blue-400">💧</span>
              </div>
              <div className="grid grid-cols-2 gap-1.5 text-xs">
                <div className="bg-green-900/30 border border-green-700 rounded p-1.5">
                  <p className="font-semibold text-green-400 text-xs">✅ WIN: +10</p>
                  <p className="text-gray-300 text-xs">Advantage in cycle</p>
                </div>
                <div className="bg-red-900/30 border border-red-700 rounded p-1.5">
                  <p className="font-semibold text-red-400 text-xs">❌ LOSE: -5</p>
                  <p className="text-gray-300 text-xs">From attack power</p>
                </div>
                <div className="bg-amber-900/20 border border-amber-700 rounded p-1.5">
                  <p className="font-semibold text-amber-400 text-xs">⚖️ DRAW: 0</p>
                  <p className="text-gray-300 text-xs">Same origami</p>
                </div>
                <div className="bg-purple-900/20 border border-purple-700 rounded p-1.5">
                  <p className="font-semibold text-purple-400 text-xs">➖ NEUTRAL: -5</p>
                  <p className="text-gray-300 text-xs">Both players</p>
                </div>
              </div>
            </div>
          </AccordionItem>

          <AccordionItem
            id="buffs"
            title="Buff NFT"
            icon="🛡️"
            isOpen={isSectionOpen('buffs')}
            onToggle={() => toggleSection('buffs')}
            onCollapse={scrollToTableOfContents}
          >
            <div className="space-y-1.5 text-xs">
              <div className="grid grid-cols-2 gap-1.5">
                <div className="bg-blue-900/30 border border-blue-700 rounded p-1.5">
                  <p className="font-semibold text-blue-300 text-xs">🛡️ Shield</p>
                  <p className="text-gray-300 text-xs">-25% damage</p>
                </div>
                <div className="bg-red-900/30 border border-red-700 rounded p-1.5">
                  <p className="font-semibold text-red-300 text-xs">⚔️ Pierce</p>
                  <p className="text-gray-300 text-xs">+25% attack</p>
                </div>
                <div className="bg-yellow-900/30 border border-yellow-700 rounded p-1.5">
                  <p className="font-semibold text-yellow-300 text-xs">⚡ Haste</p>
                  <p className="text-gray-300 text-xs">+5 flat damage</p>
                </div>
                <div className="bg-cyan-900/30 border border-cyan-700 rounded p-1.5">
                  <p className="font-semibold text-cyan-300 text-xs">❄️ Frost</p>
                  <p className="text-gray-300 text-xs">Freeze (1x use)</p>
                </div>
              </div>
              <div className="bg-purple-900/30 rounded p-1.5 space-y-0.5">
                <p className="text-purple-200 font-semibold">Chance to activate RNG:</p>
                <p className="text-purple-200 text-xs">1 Buff: Frost 30% | Shield 40% | Pierce 50% | Haste 60%</p>
                <p className="text-purple-200 font-semibold mt-1 text-xs">2 Buffs:</p>
                <p className="text-purple-200 text-xs">Pos1: Frost 20% | Shield 40% | Pierce 50% | Haste 60%</p>
                <p className="text-green-400 text-xs">If 1st fails → Pos2 boosted: 40% | 60% | 80% | 90%</p>
                <p className="text-amber-400 text-xs">If 1st succeeds → Pos2 reduced: 10% | 20% | 25% | 30%</p>
              </div>
            </div>
          </AccordionItem>

          <AccordionItem
            id="potions"
            title="Potion NFT"
            icon="🧪"
            isOpen={isSectionOpen('potions')}
            onToggle={() => toggleSection('potions')}
            onCollapse={scrollToTableOfContents}
          >
            <div className="space-y-1.5 text-xs">
              {/* AGC: Mint Panel - First */}
              <div className="bg-blue-900/20 border border-blue-700 rounded p-1.5">
                <p className="text-blue-300 font-semibold text-xs">🎲 Mint Potions:</p>
                <p className="text-blue-300 text-xs mt-0.5">Mint potions for chances: <span className="text-white">⚪ Common</span> | <span className="text-green-400">🟢 Uncommon</span> | <span className="text-blue-400">🔵 Rare</span> | <span className="text-purple-400">🟣 Epic</span> | <span className="text-orange-400">🟠 Legendary</span></p>
                <p className="text-blue-300 text-xs mt-0.5">Chances improve with FarmProScore</p>
              </div>
              
              {/* AGC: Potion Types - Second */}
              <div className="grid grid-cols-2 gap-1.5">
                <div className="bg-red-900/30 border border-red-700 rounded p-1.5">
                  <p className="font-semibold text-red-300 text-xs">❤️ Healing</p>
                  <p className="text-gray-300 text-xs">+10-20 energy</p>
                  <p className="text-gray-400 text-xs">1 turn (instant)</p>
                </div>
                <div className="bg-blue-900/30 border border-blue-700 rounded p-1.5">
                  <p className="font-semibold text-blue-300 text-xs">✨ Mana</p>
                  <p className="text-gray-300 text-xs">+5-10% buff</p>
                  <p className="text-gray-400 text-xs">All turns</p>
                </div>
                <div className="bg-purple-900/30 border border-purple-700 rounded p-1.5">
                  <p className="font-semibold text-purple-300 text-xs">⚡ Double Strike</p>
                  <p className="text-gray-300 text-xs">+25-50% dmg</p>
                  <p className="text-gray-400 text-xs">1 turn if win</p>
                </div>
                <div className="bg-green-900/30 border border-green-700 rounded p-1.5">
                  <p className="font-semibold text-green-300 text-xs">☠️ Poison</p>
                  <p className="text-gray-300 text-xs">2.5-5.0 dmg/turn</p>
                  <p className="text-gray-400 text-xs">5 turns</p>
                </div>
              </div>
              
              {/* AGC: Activation Info */}
              <div className="bg-purple-900/30 rounded p-1.5">
                <p className="text-purple-200 font-semibold text-xs">Activation:</p>
                <p className="text-purple-200 text-xs">No RNG - guaranteed effect (except Double Strike requires winning turn)</p>
              </div>
              
              {/* AGC: Rarities - Third */}
              <div className="grid grid-cols-2 gap-1">
                <div className="bg-white/10 border border-white rounded p-1">
                  <p className="font-semibold text-white text-xs">⚪ Common</p>
                  <p className="text-gray-300 text-xs">1.00× effect</p>
                </div>
                <div className="bg-green-900/30 border border-green-500 rounded p-1">
                  <p className="font-semibold text-green-400 text-xs">🟢 Uncommon</p>
                  <p className="text-gray-300 text-xs">1.10× | 70-98%</p>
                </div>
                <div className="bg-blue-900/30 border border-blue-500 rounded p-1">
                  <p className="font-semibold text-blue-400 text-xs">🔵 Rare</p>
                  <p className="text-gray-300 text-xs">1.20× | 30-55%</p>
                </div>
                <div className="bg-purple-900/30 border border-purple-500 rounded p-1">
                  <p className="font-semibold text-purple-400 text-xs">🟣 Epic</p>
                  <p className="text-gray-300 text-xs">1.50× | 10-20%</p>
                </div>
                <div className="bg-orange-900/30 border border-orange-500 rounded p-1 col-span-2">
                  <p className="font-semibold text-orange-400 text-xs">🟠 Legendary</p>
                  <p className="text-gray-300 text-xs">2.00× | 1-3%</p>
                </div>
              </div>
            </div>
          </AccordionItem>

          <AccordionItem
            id="rarity"
            title="Rarity"
            icon="💎"
            isOpen={isSectionOpen('rarity')}
            onToggle={() => toggleSection('rarity')}
            onCollapse={scrollToTableOfContents}
          >
            <div className="space-y-1.5 text-xs">
              <div className="bg-blue-900/20 border border-blue-700 rounded p-1.5">
                <p className="text-blue-300 text-xs">Sometimes depending on RNG, origami appears in rare form and gives you extra damage bonus.</p>
              </div>
              <div className="grid grid-cols-2 gap-1.5 text-xs">
              <div className="bg-cyan-900/30 border border-cyan-500 rounded p-1.5">
                <p className="font-semibold text-cyan-300">💎 DIAMOND</p>
                <p className="text-gray-300">+50 | 0.1%</p>
              </div>
              <div className="bg-yellow-900/30 border border-yellow-500 rounded p-1.5">
                <p className="font-semibold text-yellow-300">🥇 GOLD</p>
                <p className="text-gray-300">+25 | 1.0%</p>
              </div>
              <div className="bg-gray-700/30 border border-gray-400 rounded p-1.5">
                <p className="font-semibold text-gray-300">🥈 SILVER</p>
                <p className="text-gray-300">+10 | 5.0%</p>
              </div>
              <div className="bg-orange-900/30 border border-orange-600 rounded p-1.5">
                <p className="font-semibold text-orange-300">🥉 BRONZE</p>
                <p className="text-gray-300">+5 | 15.0%</p>
              </div>
              <div className="bg-gray-900/50 border border-gray-600 rounded p-1.5 col-span-2">
                <p className="font-semibold text-gray-400">⚪ NONE</p>
                <p className="text-gray-300">+0 | 78.9%</p>
              </div>
              </div>
            </div>
          </AccordionItem>

          <AccordionItem
            id="leveling"
            title="Leveling"
            icon="📈"
            isOpen={isSectionOpen('leveling')}
            onToggle={() => toggleSection('leveling')}
            onCollapse={scrollToTableOfContents}
          >
            <div className="space-y-1 text-xs">
              <div className="grid grid-cols-2 gap-1">
                <div>
                  <p className="text-gray-300">⚔️ Attack:</p>
                  <p className="text-gray-400">+1 per level</p>
                </div>
                <div>
                  <p className="text-gray-300">🛡️ Defense:</p>
                  <p className="text-gray-400">+0.5 per level</p>
                </div>
                <div>
                  <p className="text-gray-300">💪 Health:</p>
                  <p className="text-gray-400">+10 per level</p>
                </div>
                <div>
                  <p className="text-gray-300">🎯 XP:</p>
                  <p className="text-gray-400">Win 50 | Draw 25 | Loss 15</p>
                </div>
              </div>
              <div className="bg-gray-900/50 rounded p-1.5 mt-1">
                <p className="text-gray-300 text-xs">PvE: 50% of PvP XP</p>
                <p className="text-gray-300 text-xs">+10% per level above | -5% per level below</p>
              </div>
            </div>
          </AccordionItem>

          <AccordionItem
            id="damage"
            title="Damage"
            icon="⚔️"
            isOpen={isSectionOpen('damage')}
            onToggle={() => toggleSection('damage')}
            onCollapse={scrollToTableOfContents}
          >
            <div className="space-y-1 text-xs">
              <div className="grid grid-cols-3 gap-1">
                <div className="bg-blue-900/30 border border-blue-500 rounded p-1">
                  <p className="text-blue-300 font-semibold">Base</p>
                  <p className="text-gray-300">Win: 30</p>
                  <p className="text-gray-300">Draw: 15</p>
                  <p className="text-gray-300">Lose: 10</p>
                </div>
                <div className="bg-green-900/30 border border-green-500 rounded p-1">
                  <p className="text-green-300 font-semibold">+Level</p>
                  <p className="text-gray-300">+AttackerLevel</p>
                </div>
                <div className="bg-cyan-900/30 border border-cyan-500 rounded p-1">
                  <p className="text-cyan-300 font-semibold">+Element</p>
                  <p className="text-gray-300">Win: +10</p>
                  <p className="text-gray-300">Lose: -5</p>
                </div>
                <div className="bg-amber-900/30 border border-amber-500 rounded p-1">
                  <p className="text-amber-300 font-semibold">+Rarity</p>
                  <p className="text-gray-300">0-50 bonus</p>
                </div>
                <div className="bg-yellow-900/30 border border-yellow-500 rounded p-1">
                  <p className="text-yellow-300 font-semibold">+Buffs</p>
                  <p className="text-gray-300">Haste: +5</p>
                  <p className="text-gray-300">Pierce: ×1.25</p>
                </div>
                <div className="bg-orange-900/30 border border-orange-500 rounded p-1">
                  <p className="text-orange-300 font-semibold">Defense</p>
                  <p className="text-gray-300">1 - (Level/200)</p>
                  <p className="text-gray-300">Min: 0.50</p>
                </div>
              </div>
            </div>
          </AccordionItem>

          <AccordionItem
            id="economics"
            title="Economics"
            icon="💰"
            isOpen={isSectionOpen('economics')}
            onToggle={() => toggleSection('economics')}
            onCollapse={scrollToTableOfContents}
          >
            <div className="space-y-1 text-xs">
              <div className="grid grid-cols-3 gap-1">
                <div className="bg-green-900/30 border border-green-700 rounded p-1.5">
                  <p className="font-semibold text-green-300">🏆 WIN</p>
                  <p className="text-gray-300">Cost: 50</p>
                  <p className="text-gray-300">Reward: 85</p>
                  <p className="text-green-400 font-bold">+35 profit</p>
                </div>
                <div className="bg-red-900/30 border border-red-700 rounded p-1.5">
                  <p className="font-semibold text-red-300">💀 LOSE</p>
                  <p className="text-gray-300">Cost: 50</p>
                  <p className="text-gray-300">Reward: 0</p>
                  <p className="text-red-400 font-bold">-50 loss</p>
                </div>
                <div className="bg-amber-900/30 border border-amber-700 rounded p-1.5">
                  <p className="font-semibold text-amber-300">⚖️ DRAW</p>
                  <p className="text-gray-300">Cost: 50</p>
                  <p className="text-gray-300">Reward: 40</p>
                  <p className="text-amber-400 font-bold">-10 loss</p>
                </div>
              </div>
              <div className="bg-gray-900/50 rounded p-1.5">
                <p className="text-orange-300 font-semibold">🔥 Burns:</p>
                <p className="text-gray-300">Win/Loss: 15 OGB | Draw: 20 OGB</p>
              </div>
            </div>
          </AccordionItem>

          <AccordionItem
            id="tournaments"
            title="Tournaments"
            icon="🏆"
            isOpen={isSectionOpen('tournaments')}
            onToggle={() => toggleSection('tournaments')}
            onCollapse={scrollToTableOfContents}
          >
            <div className="space-y-1 text-xs">
              <div className="grid grid-cols-2 gap-1">
                <div className="bg-slate-900/50 border border-slate-600 rounded p-1.5">
                  <p className="text-blue-300 font-semibold">Players</p>
                  <p className="text-gray-300">Min: 3 | Max: 10</p>
                  <p className="text-gray-300">Round-robin</p>
                </div>
                <div className="bg-slate-900/50 border border-slate-600 rounded p-1.5">
                  <p className="text-green-300 font-semibold">Entry</p>
                  <p className="text-gray-300">50 OGB per player</p>
                  <p className="text-gray-300">On-chain</p>
                </div>
                <div className="bg-slate-900/50 border border-slate-600 rounded p-1.5">
                  <p className="text-yellow-300 font-semibold">Prizes</p>
                  <p className="text-gray-300">1st: 150 | 2nd: 100 | 3rd: 50</p>
                  <p className="text-gray-300">Treasury-backed</p>
                </div>
                <div className="bg-slate-900/50 border border-slate-600 rounded p-1.5">
                  <p className="text-purple-300 font-semibold">Tie-break</p>
                  <p className="text-gray-300">Wins → Level → Win Rate</p>
                </div>
              </div>
            </div>
          </AccordionItem>

          <AccordionItem
            id="tips"
            title="Tips"
            icon="🎯"
            isOpen={isSectionOpen('tips')}
            onToggle={() => toggleSection('tips')}
            onCollapse={scrollToTableOfContents}
          >
            <div className="space-y-1 text-xs">
              <div className="grid grid-cols-2 gap-1">
                <div>
                  <p className="text-purple-300 font-semibold">🧠 Strategy:</p>
                  <p className="text-gray-300">• Counter elements</p>
                  <p className="text-gray-300">• Save Frost for clutch</p>
                  <p className="text-gray-300">• Level up consistently</p>
                </div>
                <div>
                  <p className="text-cyan-300 font-semibold">💎 Advanced:</p>
                  <p className="text-gray-300">• Pierce vs high-level</p>
                  <p className="text-gray-300">• Shield for defense</p>
                  <p className="text-gray-300">• Aim 60%+ win rate</p>
                </div>
              </div>
            </div>
          </AccordionItem>
        </div>
      </div>
    </main>
  )
}

